import { useState, useEffect, useCallback } from 'react';
import { Dialog, DialogContent, DialogTitle } from './ui/dialog';
import { Button } from './ui/button';
import { Checkbox } from './ui/checkbox';
import { Progress } from './ui/progress';
import {
  Hotel, Shield, LayoutDashboard, Users, Calendar, 
  DoorOpen, Receipt, Sparkles, BarChart3, Rocket,
  ChevronLeft, ChevronRight, X
} from 'lucide-react';
import { useLanguage } from '@/contexts/LanguageContext';
import { Link } from 'react-router-dom';

// Walkthrough slides data - easily localizable
const WALKTHROUGH_SLIDES = [
  {
    id: 1,
    titleKey: 'walkthrough.welcome.title',
    titleDefault: 'Welcome to Merina HMS',
    contentKey: 'walkthrough.welcome.content',
    contentDefault: 'Your all-in-one Hotel Management System to manage bookings, guests, billing, and operations effortlessly.',
    icon: Hotel,
    color: 'from-purple-500 to-indigo-600',
    bgPattern: 'radial-gradient(circle at 20% 80%, rgba(147, 51, 234, 0.15) 0%, transparent 50%)'
  },
  {
    id: 2,
    titleKey: 'walkthrough.security.title',
    titleDefault: 'Secure Access',
    contentKey: 'walkthrough.security.content',
    contentDefault: 'Log in using your authorized credentials to access your hotel dashboard securely.',
    icon: Shield,
    color: 'from-emerald-500 to-teal-600',
    bgPattern: 'radial-gradient(circle at 80% 20%, rgba(16, 185, 129, 0.15) 0%, transparent 50%)'
  },
  {
    id: 3,
    titleKey: 'walkthrough.dashboard.title',
    titleDefault: 'Smart Dashboard',
    contentKey: 'walkthrough.dashboard.content',
    contentDefault: 'View room availability, occupancy, check-ins, and key hotel metrics in real time.',
    icon: LayoutDashboard,
    color: 'from-blue-500 to-cyan-600',
    bgPattern: 'radial-gradient(circle at 50% 50%, rgba(59, 130, 246, 0.15) 0%, transparent 50%)'
  },
  {
    id: 4,
    titleKey: 'walkthrough.guests.title',
    titleDefault: 'Guest Profiles',
    contentKey: 'walkthrough.guests.content',
    contentDefault: 'Store and manage guest details, ID documents, and stay history efficiently.',
    icon: Users,
    color: 'from-amber-500 to-orange-600',
    bgPattern: 'radial-gradient(circle at 30% 70%, rgba(245, 158, 11, 0.15) 0%, transparent 50%)'
  },
  {
    id: 5,
    titleKey: 'walkthrough.reservations.title',
    titleDefault: 'Reservations',
    contentKey: 'walkthrough.reservations.content',
    contentDefault: 'Create, modify, and track room bookings with ease.',
    icon: Calendar,
    color: 'from-pink-500 to-rose-600',
    bgPattern: 'radial-gradient(circle at 70% 30%, rgba(236, 72, 153, 0.15) 0%, transparent 50%)'
  },
  {
    id: 6,
    titleKey: 'walkthrough.frontdesk.title',
    titleDefault: 'Front Desk Operations',
    contentKey: 'walkthrough.frontdesk.content',
    contentDefault: 'Handle guest check-ins and check-outs smoothly with automated workflows.',
    icon: DoorOpen,
    color: 'from-violet-500 to-purple-600',
    bgPattern: 'radial-gradient(circle at 20% 30%, rgba(139, 92, 246, 0.15) 0%, transparent 50%)'
  },
  {
    id: 7,
    titleKey: 'walkthrough.billing.title',
    titleDefault: 'Billing & Invoices',
    contentKey: 'walkthrough.billing.content',
    contentDefault: 'Generate invoices, track payments, and manage taxes accurately.',
    icon: Receipt,
    color: 'from-green-500 to-emerald-600',
    bgPattern: 'radial-gradient(circle at 80% 80%, rgba(34, 197, 94, 0.15) 0%, transparent 50%)'
  },
  {
    id: 8,
    titleKey: 'walkthrough.housekeeping.title',
    titleDefault: 'Housekeeping Management',
    contentKey: 'walkthrough.housekeeping.content',
    contentDefault: 'Assign room cleaning tasks and track room status in real time.',
    icon: Sparkles,
    color: 'from-sky-500 to-blue-600',
    bgPattern: 'radial-gradient(circle at 50% 20%, rgba(14, 165, 233, 0.15) 0%, transparent 50%)'
  },
  {
    id: 9,
    titleKey: 'walkthrough.reports.title',
    titleDefault: 'Business Insights',
    contentKey: 'walkthrough.reports.content',
    contentDefault: 'View financial, occupancy, and operational reports for better decision-making.',
    icon: BarChart3,
    color: 'from-indigo-500 to-violet-600',
    bgPattern: 'radial-gradient(circle at 30% 50%, rgba(99, 102, 241, 0.15) 0%, transparent 50%)'
  },
  {
    id: 10,
    titleKey: 'walkthrough.getstarted.title',
    titleDefault: "You're Ready!",
    contentKey: 'walkthrough.getstarted.content',
    contentDefault: 'Log in now and start managing your hotel efficiently with Merina HMS.',
    icon: Rocket,
    color: 'from-purple-500 to-pink-600',
    bgPattern: 'radial-gradient(circle at 60% 60%, rgba(168, 85, 247, 0.2) 0%, transparent 50%)'
  }
];

const STORAGE_KEY = 'merina_walkthrough_seen';

/**
 * LoginWalkthrough - 10-slide tutorial modal for first-time users
 * 
 * Features:
 * - Skip button
 * - Next/Previous navigation
 * - Progress indicator
 * - Don't show again checkbox
 * - Mobile responsive
 * - Keyboard accessible
 * - Localization ready (uses LanguageContext directly)
 */
export default function LoginWalkthrough({ isOpen: controlledOpen, onOpenChange }) {
  const { t } = useLanguage();
  const [currentSlide, setCurrentSlide] = useState(0);
  const [dontShowAgain, setDontShowAgain] = useState(false);
  const [internalOpen, setInternalOpen] = useState(false);

  // Determine if component is controlled or uncontrolled
  const isControlled = controlledOpen !== undefined;
  const isOpen = isControlled ? controlledOpen : internalOpen;
  const setOpen = isControlled ? onOpenChange : setInternalOpen;

  // Check localStorage on mount for uncontrolled mode
  useEffect(() => {
    if (!isControlled) {
      const hasSeenWalkthrough = localStorage.getItem(STORAGE_KEY);
      if (!hasSeenWalkthrough) {
        // Small delay for better UX
        const timer = setTimeout(() => setInternalOpen(true), 500);
        return () => clearTimeout(timer);
      }
    }
  }, [isControlled]);

  const handleClose = useCallback(() => {
    if (dontShowAgain) {
      localStorage.setItem(STORAGE_KEY, 'true');
    }
    setCurrentSlide(0);
    setOpen(false);
  }, [dontShowAgain, setOpen]);

  // Keyboard navigation
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (!isOpen) return;
      
      if (e.key === 'ArrowRight' || e.key === 'Enter') {
        if (currentSlide < WALKTHROUGH_SLIDES.length - 1) {
          setCurrentSlide(prev => prev + 1);
        }
      } else if (e.key === 'ArrowLeft') {
        if (currentSlide > 0) {
          setCurrentSlide(prev => prev - 1);
        }
      } else if (e.key === 'Escape') {
        handleClose();
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [isOpen, currentSlide, handleClose]);

  const handleSkip = useCallback(() => {
    localStorage.setItem(STORAGE_KEY, 'true');
    setCurrentSlide(0);
    setOpen(false);
  }, [setOpen]);

  const handleNext = useCallback(() => {
    if (currentSlide < WALKTHROUGH_SLIDES.length - 1) {
      setCurrentSlide(prev => prev + 1);
    } else {
      handleClose();
    }
  }, [currentSlide, handleClose]);

  const handlePrev = useCallback(() => {
    if (currentSlide > 0) {
      setCurrentSlide(prev => prev - 1);
    }
  }, [currentSlide]);

  const handleDotClick = useCallback((index) => {
    setCurrentSlide(index);
  }, []);

  // Get translated text or default
  const getText = (key, defaultText) => {
    if (t && typeof t === 'function') {
      const translated = t(key);
      return translated !== key ? translated : defaultText;
    }
    return defaultText;
  };

  const slide = WALKTHROUGH_SLIDES[currentSlide];
  const IconComponent = slide.icon;
  const progress = ((currentSlide + 1) / WALKTHROUGH_SLIDES.length) * 100;
  const isLastSlide = currentSlide === WALKTHROUGH_SLIDES.length - 1;
  const isFirstSlide = currentSlide === 0;

  return (
    <Dialog open={isOpen} onOpenChange={setOpen}>
      <DialogContent 
        className="sm:max-w-lg md:max-w-xl p-0 gap-0 overflow-hidden border-0 shadow-2xl"
        onInteractOutside={(e) => e.preventDefault()}
        hideCloseButton={true}
        data-testid="walkthrough-modal"
      >
        {/* Visually hidden title for accessibility */}
        <DialogTitle className="sr-only">
          {getText('walkthrough.modalTitle', 'Merina HMS Welcome Walkthrough')}
        </DialogTitle>
        {/* Progress Bar */}
        <div className="absolute top-0 left-0 right-0 z-10">
          <Progress value={progress} className="h-1 rounded-none" />
        </div>

        {/* Skip Button */}
        <Button
          variant="ghost"
          size="sm"
          onClick={handleSkip}
          className="absolute top-3 right-3 z-20 text-gray-500 hover:text-gray-700"
          aria-label="Skip walkthrough"
          data-testid="walkthrough-skip-btn"
        >
          <X className="w-4 h-4 mr-1" />
          <span className="hidden sm:inline">{getText('walkthrough.skip', 'Skip')}</span>
        </Button>

        {/* Slide Content */}
        <div 
          className="relative min-h-[420px] sm:min-h-[480px] flex flex-col"
          style={{ background: slide.bgPattern }}
          role="tabpanel"
          aria-label={`Slide ${currentSlide + 1} of ${WALKTHROUGH_SLIDES.length}`}
        >
          {/* Icon Section */}
          <div className="flex-1 flex flex-col items-center justify-center px-6 pt-8 pb-6">
            {/* Clickable Logo */}
            <Link to="/home" className="mb-6 hover:opacity-80 transition-opacity" aria-label="Go to Merina HMS home page">
              <img src="/merina-logo-md.png" alt="Merina HMS" className="h-14 sm:h-16 w-auto object-contain" />
            </Link>

            {/* Title */}
            <h2 
              className="text-2xl sm:text-3xl font-bold text-gray-900 text-center mb-3"
              style={{ fontFamily: 'Space Grotesk, sans-serif' }}
            >
              {getText(slide.titleKey, slide.titleDefault)}
            </h2>

            {/* Content */}
            <p className="text-gray-600 text-center text-base sm:text-lg max-w-md leading-relaxed px-4">
              {getText(slide.contentKey, slide.contentDefault)}
            </p>

            {/* Slide Number Badge */}
            <div className="mt-6 px-3 py-1 bg-gray-100 rounded-full">
              <span className="text-sm font-medium text-gray-600">
                {currentSlide + 1} / {WALKTHROUGH_SLIDES.length}
              </span>
            </div>
          </div>

          {/* Navigation Section */}
          <div className="px-6 pb-6 space-y-4">
            {/* Dot Indicators */}
            <div 
              className="flex justify-center gap-2" 
              role="tablist"
              aria-label="Walkthrough slides"
            >
              {WALKTHROUGH_SLIDES.map((_, index) => (
                <button
                  key={index}
                  onClick={() => handleDotClick(index)}
                  className={`w-2 h-2 rounded-full transition-all duration-300 ${
                    index === currentSlide 
                      ? 'w-6 bg-purple-600' 
                      : index < currentSlide 
                        ? 'bg-purple-400' 
                        : 'bg-gray-300 hover:bg-gray-400'
                  }`}
                  role="tab"
                  aria-selected={index === currentSlide}
                  aria-label={`Go to slide ${index + 1}`}
                  data-testid={`walkthrough-dot-${index}`}
                />
              ))}
            </div>

            {/* Navigation Buttons */}
            <div className="flex items-center justify-between gap-3">
              <Button
                variant="outline"
                onClick={handlePrev}
                disabled={isFirstSlide}
                className="flex-1 sm:flex-none"
                aria-label="Previous slide"
                data-testid="walkthrough-prev-btn"
              >
                <ChevronLeft className="w-4 h-4 mr-1" />
                <span className="hidden sm:inline">{getText('walkthrough.back', 'Previous')}</span>
                <span className="sm:hidden">{getText('walkthrough.back', 'Back')}</span>
              </Button>

              <Button
                onClick={handleNext}
                className={`flex-1 sm:flex-none bg-gradient-to-r ${slide.color} hover:opacity-90`}
                aria-label={isLastSlide ? "Get started" : "Next slide"}
                data-testid="walkthrough-next-btn"
              >
                {isLastSlide ? (
                  <>
                    <span>{getText('walkthrough.getStartedButton', 'Get Started')}</span>
                    <Rocket className="w-4 h-4 ml-1" />
                  </>
                ) : (
                  <>
                    <span>{getText('walkthrough.next', 'Next')}</span>
                    <ChevronRight className="w-4 h-4 ml-1" />
                  </>
                )}
              </Button>
            </div>

            {/* Don't Show Again */}
            <div className="flex items-center justify-center gap-3 pt-2 min-h-[48px]">
              <Checkbox
                id="dontShowAgain"
                checked={dontShowAgain}
                onCheckedChange={setDontShowAgain}
                data-testid="walkthrough-dont-show-checkbox"
                className="h-6 w-6 min-w-[24px] min-h-[24px]"
              />
              <label 
                htmlFor="dontShowAgain" 
                className="text-sm sm:text-base text-gray-500 cursor-pointer select-none leading-tight"
              >
                {getText('walkthrough.dontShowAgain', "Don't show this again")}
              </label>
            </div>
          </div>
        </div>

        {/* CSS Animation */}
        <style>{`
          @keyframes slideIn {
            from {
              opacity: 0;
              transform: translateY(20px) scale(0.9);
            }
            to {
              opacity: 1;
              transform: translateY(0) scale(1);
            }
          }
        `}</style>
      </DialogContent>
    </Dialog>
  );
}

// Export slides data for potential use elsewhere (e.g., help section)
export { WALKTHROUGH_SLIDES };
